import {
  ERROR_CODES,
  GAME_NAME,
  HINTS_PER_MATCH,
  INITIAL_HP,
  ITEM_SCRAMBLE,
  MAX_CANDIDATE_QUERIES_PER_ROUND,
  MIN_ABBR_SYLLABLES,
  SCRAMBLE_ITEMS_PER_MATCH,
  countAbbrSyllables,
  honorPromoted,
  localPrecheck,
  type ErrorPayload,
  type GameOverPayload,
  type HintResultPayload,
  type ItemAppliedPayload,
  type PunishmentPayload,
  type RejectReason,
  type RoomSnapshotPayload,
  type RoundStartPayload,
  type ScrambleEffect,
  type ServerMessage,
} from "@gaoya/shared";
import { SFX } from "../audio/sfx.js";
import { persistAudioPrefs } from "../audio/prefs.js";
import { clearResume, loadStay, saveResume, saveStay } from "./resume.js";
import { drawShareCard, SHARE_H, SHARE_W } from "../render/share-card.js";
import { leaderboardPanel } from "../render/screens/leaderboard.js";
import type { Platform } from "../platform/types.js";
import { GameSocket, asPayload } from "../net/socket.js";
import {
  banner,
  isMyTurn,
  resetBattleInput,
  resetRoom,
  toast,
  clipNickname,
  type LeaderRow,
  type UiState,
} from "./state.js";

const REJECT_TEXT: Record<RejectReason, string> = {
  INVALID_FORMAT: "格式不对，需四字成语",
  NOT_IN_DICT: "词库未收录",
  ALREADY_USED: "本局已经用过",
  WRONG_PINYIN: "读音接不上",
};

/** 从分享卡片进入才自动入席。开发者工具「编译/重载」默认 scene=1001，即使残留 room= 也不进房。 */
const SHARE_INVITE_SCENES = new Set([
  1007, // 单聊小程序消息卡片
  1008, // 群聊小程序消息卡片
  1036, // App 分享消息卡片
  1044, // 带 shareTicket 的卡片
  1096, // 聊天记录
]);

export class Session {
  private countedWinRoom = "";
  private resumeFallbackId: ReturnType<typeof setTimeout> | null = null;
  private pendingJoinCode = "";
  private authLoginRetry = false;
  /** 上次停在雅间/对局，重载可拉回。 */
  private allowRoomRestore: boolean;
  /** 本局已主动开房 / 匹配 / 入席，服务端回房消息要收下。 */
  private liveIntent = false;

  constructor(
    private platform: Platform,
    private socket: GameSocket,
    private ui: UiState,
  ) {
    this.allowRoomRestore = loadStay((key) => platform.getStorage(key)) === "room";
  }

  enterTeahouse(): void {
    const nick = this.ui.nickname.trim();
    if (this.ui.useWxProfile && !this.ui.privacyAgreed) {
      this.openPrivacyPrompt();
      toast(this.ui, "请先阅读并同意隐私政策");
      return;
    }
    if (this.ui.useWxProfile && !this.ui.wxProfileReady) {
      toast(this.ui, "请先授权微信头像和昵称");
      return;
    }
    if (nick.length < 1 || nick.length > 16) {
      toast(this.ui, this.ui.useWxProfile ? "请授权微信昵称" : "请起一个 1–16 字雅号");
      return;
    }
    if (this.ui.authed) {
      this.ui.screen = "hall";
      this.platform.hideUserInfoButton();
      return;
    }
    this.ui.connecting = true;
    this.ui.connHint = this.hasResume()
      ? "正在恢复对局…"
      : this.ui.useWxProfile
        ? "正在用微信身份入座…"
        : "正在推门…";
    this.socket.start();
  }

  onConnected(): void {
    if (this.allowRoomRestore && this.hasResume()) {
      this.ui.connecting = true;
      this.ui.connHint = "正在恢复对局…";
      this.socket.send("RECONNECT", { token: this.ui.token, roomId: this.ui.roomId });
      return;
    }
    void this.sendAuth();
  }

  private async sendAuth(): Promise<void> {
    let code = `dev:${this.ui.nickname}`;
    if (this.platform.kind === "wechat" && !this.ui.authDevFallback) {
      const wxCode = await this.platform.getLoginCode();
      if (wxCode) code = wxCode;
    }
    this.socket.send("AUTH", {
      code,
      nickname: this.ui.nickname,
      avatar: this.ui.avatarUrl,
    });
  }

  onDisconnected(): void {
    this.ui.authed = false;
    if (this.ui.screen === "battle") {
      toast(this.ui, "茶线断了，正在重连…", 4000);
    }
  }

  /** 对局内重连走 RECONNECT，服务端不回 AUTH_OK；房间消息即表示本连接已鉴权。 */
  private markSocketAuthed(): void {
    if (this.ui.userId && this.ui.token) this.ui.authed = true;
  }

  handle(msg: ServerMessage): void {
    switch (msg.action) {
      case "AUTH_OK":
        this.onAuthOk(msg);
        break;
      case "ROOM_CREATED":
        this.onRoomCreated(msg);
        break;
      case "ROOM_LOBBY":
        this.onRoomLobby(msg);
        break;
      case "MATCH_SUCCESS":
        this.onMatchSuccess(msg);
        break;
      case "ROUND_START":
        this.onRoundStart(msg);
        break;
      case "CANDIDATES":
        this.onCandidates(msg);
        break;
      case "HINT_RESULT":
        this.onHintResult(msg);
        break;
      case "ITEM_APPLIED":
        this.onItemApplied(msg);
        break;
      case "ACTION_RESULT":
        this.onActionResult(msg);
        break;
      case "PUNISHMENT":
        this.onPunishment(msg);
        break;
      case "GAME_OVER":
        this.onGameOver(msg);
        break;
      case "ROOM_SNAPSHOT":
        this.onSnapshot(msg);
        break;
      case "OPPONENT_OFFLINE":
        this.onOppOffline(msg);
        break;
      case "OPPONENT_ONLINE":
        this.onOppOnline(msg);
        break;
      case "MATCH_TIMEOUT":
        toast(this.ui, "一时寻不到茶客，请稍后再试");
        this.ui.screen = "hall";
        break;
      case "SERVER_SHUTDOWN": {
        const p = asPayload(msg, "SERVER_SHUTDOWN");
        toast(this.ui, p.message || "茶馆打烊整顿，对局将尽快结束", 5000);
        break;
      }
      case "ERROR":
        this.onError(asPayload(msg, "ERROR"));
        break;
      case "PONG": {
        const p = asPayload(msg, "PONG");
        this.syncClock(p.serverTime);
        break;
      }
      default:
        break;
    }
  }

  match(): void {
    this.noteLiveIntent();
    this.ui.screen = "matching";
    this.ui.matchingSince = this.platform.now();
    this.socket.send("MATCH_REQ", { mode: "CHAIN" });
  }

  cancelMatch(): void {
    this.socket.send("CANCEL_MATCH", {});
    this.markAwayStay();
    this.ui.screen = "hall";
  }

  createRoom(): void {
    this.noteLiveIntent();
    this.markRoomStay();
    this.ui.privateMode = "wait";
    this.ui.roomCode = "";
    this.ui.mode = "CHAIN";
    this.ui.publicMatch = false;
    this.ui.screen = "private";
    this.socket.send("CREATE_ROOM", { mode: "CHAIN" });
  }

  createRushRoom(maxSeats: 3 | 4 = 4): void {
    this.noteLiveIntent();
    this.markRoomStay();
    this.ui.privateMode = "wait";
    this.ui.roomCode = "";
    this.ui.mode = "RUSH";
    this.ui.publicMatch = false;
    this.ui.lobbyMaxSeats = maxSeats;
    this.ui.lobbySeats = [];
    this.ui.lobbyCountdownDeadline = 0;
    this.ui.lobbyReadyDeadline = 0;
    this.ui.screen = "private";
    this.socket.send("CREATE_ROOM", { mode: "RUSH", maxSeats });
  }

  matchRush(maxSeats: 3 | 4): void {
    this.noteLiveIntent();
    this.markRoomStay();
    this.ui.hallMenu = "home";
    this.ui.privateMode = "wait";
    this.ui.roomCode = "";
    this.ui.mode = "RUSH";
    this.ui.publicMatch = true;
    this.ui.lobbyMaxSeats = maxSeats;
    this.ui.lobbySeats = [];
    this.ui.lobbyCountdownDeadline = 0;
    this.ui.lobbyReadyDeadline = 0;
    this.ui.screen = "private";
    this.socket.send("MATCH_REQ", { mode: "RUSH", maxSeats });
  }

  toggleReady(): void {
    if (this.ui.mode !== "RUSH" || this.ui.screen !== "private") return;
    const ready = this.ui.lobbySeats.some((s) => s.id === this.ui.userId && s.ready);
    this.socket.send("READY", { ready: !ready });
  }

  openJoin(): void {
    this.ui.privateMode = "join";
    this.ui.joinInput = "";
    this.ui.screen = "private";
  }

  joinRoom(): void {
    const code = this.ui.joinInput.trim();
    if (!/^\d{6}$/.test(code)) {
      toast(this.ui, "请输入 6 位房间号");
      return;
    }
    this.noteLiveIntent();
    this.socket.send("JOIN_ROOM", { roomCode: code });
  }

  queryIfReady(): void {
    const ui = this.ui;
    if (!isMyTurn(ui) || !ui.roomId) return;
    if (countAbbrSyllables(ui.abbr) < MIN_ABBR_SYLLABLES) {
      ui.candidates = [];
      return;
    }
    if (ui.lastQueriedAbbr === ui.abbr) return;
    if (ui.queriesThisRound >= MAX_CANDIDATE_QUERIES_PER_ROUND) return;
    ui.lastQueriedAbbr = ui.abbr;
    ui.queriesThisRound += 1;
    this.socket.send("QUERY_CANDIDATES", {
      roomId: ui.roomId,
      round: ui.round,
      abbr: ui.abbr,
    });
  }

  submitWord(word: string): void {
    const ui = this.ui;
    if (!isMyTurn(ui) || !ui.roomId) {
      toast(ui, "还没轮到你");
      return;
    }
    if (!localPrecheck(word)) {
      toast(ui, "请点选四字成语");
      return;
    }
    this.socket.send("SUBMIT_WORD", {
      roomId: ui.roomId,
      round: ui.round,
      word,
    });
  }

  useHint(): void {
    const ui = this.ui;
    const now = this.platform.now();
    if (!isMyTurn(ui) || !ui.roomId) {
      toast(ui, "还没轮到你");
      return;
    }
    if (ui.hintsLeft <= 0) {
      toast(ui, "本局锦囊已经用完");
      return;
    }
    if (ui.hintPending && now < ui.hintPendingUntil) {
      toast(ui, "锦囊翻开中…");
      return;
    }
    ui.hintPending = true;
    ui.hintPendingUntil = now + 3000;
    toast(ui, "正在翻锦囊…");
    this.socket.send("USE_HINT", { roomId: ui.roomId, round: ui.round });
  }

  useScramble(): void {
    const ui = this.ui;
    const now = this.platform.now();
    if (ui.screen !== "battle" || !ui.roomId || ui.gameOver) return;
    if (ui.mode === "RUSH") {
      toast(ui, "抢答局不设此道具");
      return;
    }
    if (ui.scrambleItemsLeft <= 0) {
      toast(ui, "本局乱序已经用完");
      return;
    }
    if (ui.oppScramble && ui.oppScramble.turnsLeft > 0) {
      toast(ui, "对方活字盘已经打乱");
      return;
    }
    if (ui.scramblePending && now < ui.scramblePendingUntil) {
      toast(ui, "乱序生效中…");
      return;
    }
    ui.scramblePending = true;
    ui.scramblePendingUntil = now + 3000;
    this.socket.send("USE_ITEM", { roomId: ui.roomId, round: ui.round, itemId: ITEM_SCRAMBLE });
  }

  surrender(): void {
    if (!this.ui.roomId) return;
    if (this.ui.mode === "RUSH") {
      this.ui.selfRacing = false;
      toast(this.ui, "你已离席，本局观战");
    }
    this.socket.send("SURRENDER", { roomId: this.ui.roomId });
  }

  rematch(): void {
    const rush = this.ui.mode === "RUSH";
    const seats = this.ui.lobbyMaxSeats === 3 ? 3 : 4;
    const pub = this.ui.publicMatch;
    const roomId = this.ui.roomId;
    const code = this.ui.roomCode;
    if (!pub && roomId && /^\d{6}$/.test(code)) {
      this.noteLiveIntent();
      this.ui.gameOver = null;
      this.ui.privateMode = "wait";
      this.ui.screen = "private";
      this.socket.send("REMATCH", { roomId });
      return;
    }
    resetRoom(this.ui);
    if (rush) {
      if (pub) this.matchRush(seats);
      else this.createRushRoom(seats);
    } else this.match();
  }

  backHall(): void {
    this.pendingJoinCode = "";
    // 1v1 等人时客户端以前拿不到 roomId，不发 LEAVE 会导致服务端雅间一直活着。
    this.socket.send("LEAVE_ROOM", this.ui.roomId ? { roomId: this.ui.roomId } : {});
    this.leaveToHall();
  }

  private leaveToHall(): void {
    this.pendingJoinCode = "";
    this.markAwayStay();
    this.clearResumeFallback();
    this.forgetBattle();
    this.closeLeaderboard();
    this.closeSettings();
    resetRoom(this.ui);
    this.ui.privateMode = "menu";
    this.ui.hallMenu = "home";
    this.ui.screen = "hall";
    this.refreshShareMenu();
  }

  openSettings(): void {
    this.ui.showSettings = true;
  }

  openPrivacyPrompt(): void {
    this.ui.showPrivacy = true;
    this.platform.hideUserInfoButton();
  }

  agreePrivacy(): void {
    this.platform.resolvePrivacyAuthorization(true);
    void this.platform.requirePrivacyAuthorize().then((ok) => {
      if (!ok) {
        toast(this.ui, "需要同意隐私政策才能授权头像昵称");
        return;
      }
      this.ui.privacyAgreed = true;
      this.ui.showPrivacy = false;
      this.platform.setStorage("gaoya.privacyAgreed", "1");
      void this.platform.fetchAuthorizedUserInfo().then((info) => {
        if (info) this.applyWxProfile(info.nickname, info.avatar);
        else this.tryResumeAfterProfile();
      });
    });
  }

  rejectPrivacy(): void {
    this.platform.resolvePrivacyAuthorization(false);
    this.ui.showPrivacy = false;
    this.ui.privacyAgreed = false;
    toast(this.ui, "已拒绝。同意隐私政策后才能使用微信头像昵称");
  }

  openPrivacyContract(): void {
    this.platform.openPrivacyContract();
  }

  closeSettings(): void {
    this.ui.showSettings = false;
  }

  togglePref(key: "bgmMuted" | "sfxMuted" | "vibrateMuted"): void {
    this.ui[key] = !this.ui[key];
    persistAudioPrefs((key, value) => this.platform.setStorage(key, value), {
      bgmMuted: this.ui.bgmMuted,
      sfxMuted: this.ui.sfxMuted,
      vibrateMuted: this.ui.vibrateMuted,
    });
    this.platform.applyAudioPrefs();
  }

  async copyRoomCode(): Promise<void> {
    const code = this.ui.roomCode;
    if (!code) {
      toast(this.ui, "房号还没备好");
      return;
    }
    const ok = await this.platform.copyText(code);
    toast(this.ui, ok ? `已复制 ${code}` : `房号 ${code}`);
  }

  shareRoom(): void {
    const code = this.ui.roomCode;
    if (!code) {
      toast(this.ui, "房号还没备好");
      return;
    }
    const rush = this.ui.mode === "RUSH";
    const seats = this.ui.lobbyMaxSeats === 3 ? 3 : 4;
    this.platform.shareApp({
      title: rush
        ? `${GAME_NAME} · ${seats}人抢答 房号 ${code}`
        : `${GAME_NAME} · 成语接龙 房号 ${code}`,
      query: `room=${code}`,
    });
    void this.platform.copyText(code).then((ok) => {
      if (ok && this.platform.kind === "browser") toast(this.ui, `房号已复制 ${code}`);
    });
  }

  applyLaunchQuery(query: Record<string, string>, scene?: number): void {
    const room = String(query.room ?? query.roomCode ?? "").trim();
    if (!/^\d{6}$/.test(room)) return;
    if (this.platform.kind === "wechat" && (scene == null || !SHARE_INVITE_SCENES.has(scene))) return;
    this.pendingJoinCode = room;
    this.tryPendingJoin();
  }

  private roomShareTitle(): string {
    const code = this.ui.roomCode;
    if (!code) return `${GAME_NAME} · 成语接龙对决`;
    if (this.ui.mode === "RUSH") {
      const seats = this.ui.lobbyMaxSeats === 3 ? 3 : 4;
      return `${GAME_NAME} · ${seats}人抢答 房号 ${code}`;
    }
    return `${GAME_NAME} · 成语接龙 房号 ${code}`;
  }

  private refreshShareMenu(): void {
    const payload: { title: string; query?: string } = {
      title: this.ui.screen === "result"
        ? `${GAME_NAME} · ${this.ui.nickname} 的战报`
        : this.roomShareTitle(),
    };
    if (this.ui.roomCode) payload.query = `room=${this.ui.roomCode}`;
    this.platform.setShareMenu(payload);
  }

  private tryPendingJoin(): boolean {
    const code = this.pendingJoinCode;
    if (!code || !this.ui.authed || this.ui.roomId) return false;
    if (this.ui.screen === "battle" || this.ui.screen === "result") return false;
    this.pendingJoinCode = "";
    this.noteLiveIntent();
    this.ui.joinInput = code;
    this.socket.send("JOIN_ROOM", { roomCode: code });
    toast(this.ui, `正在入席 ${code}`);
    return true;
  }

  shareReport(): void {
    const handle = this.platform.createOffscreenCanvas(SHARE_W, SHARE_H);
    if (!handle) {
      this.platform.shareApp({ title: `${GAME_NAME} · ${this.ui.nickname} 的战报` });
      toast(this.ui, "已唤起分享");
      return;
    }
    drawShareCard(handle.ctx, this.ui);
    void this.platform.exportCanvasPng(handle, "接词战报.png").then((path) => {
      const payload: { title: string; imagePath?: string } = {
        title: `${GAME_NAME} · ${this.ui.nickname} 的战报`,
      };
      if (path) payload.imagePath = path;
      this.platform.shareApp(payload);
      toast(this.ui, this.platform.kind === "browser" ? "战报已保存" : "已唤起分享");
    });
  }

  fetchLeaderboard(w: number, h: number): void {
    const ui = this.ui;
    ui.showSettings = false;
    ui.showLeaderboard = true;
    const panel = leaderboardPanel(w, h, ui);
    if (this.platform.kind === "wechat") {
      ui.leaderboard = [];
      ui.leaderboardHint = "同玩好友按胜场排序";
      ui.hallRankCanvas = null;
      this.platform.showFriendRank(panel, "panel", ui.nickname);
      ui.friendRankCanvas = this.platform.getFriendRankCanvas();
      if (!ui.friendRankCanvas) ui.leaderboardHint = "请在手机微信中查看好友榜";
      return;
    }
    ui.friendRankCanvas = null;
    ui.leaderboardHint = "正在揭榜…";
    void this.fetchServerLeaderboard();
  }

  closeLeaderboard(): void {
    this.ui.showLeaderboard = false;
    this.ui.friendRankCanvas = null;
    this.ui.hallRankKey = "";
  }

  /**
   * 大厅首页的好友榜条：微信内由开放数据域按 strip 模式画好友名次；
   * 浏览器内拉一次全服榜。每帧调用，仅在可见性/尺寸变化时发消息。
   */
  syncHallRank(rows: { x: number; y: number; w: number; h: number } | null): void {
    const ui = this.ui;
    const visible = !!rows && ui.screen === "hall" && ui.hallMenu !== "rush-size" && !ui.showLeaderboard;
    const key = visible && rows ? `${Math.round(rows.w)}x${Math.round(rows.h)}|${ui.nickname}` : "";
    if (key === ui.hallRankKey) return;
    const wasVisible = ui.hallRankKey !== "";
    ui.hallRankKey = key;
    if (!visible || !rows) {
      ui.hallRankCanvas = null;
      // 打开总榜时共用同一块开放数据域画布，不要 hide 清屏，否则 iPhone 上好友会闪没。
      if (wasVisible && !ui.showLeaderboard && this.platform.kind === "wechat") this.platform.hideFriendRank();
      return;
    }
    if (this.platform.kind === "wechat") {
      this.platform.showFriendRank(rows, "strip", ui.nickname);
      ui.hallRankCanvas = this.platform.getFriendRankCanvas();
      return;
    }
    ui.hallRankCanvas = null;
    void this.prefetchServerLeaderboard();
  }

  private async prefetchServerLeaderboard(): Promise<void> {
    const res = await this.platform.httpJson("GET", `${this.platform.resolveHttpBase()}/api/leaderboard`);
    if (!res.ok || this.ui.showLeaderboard) return;
    const rows = normalizeLeader(res.body);
    if (rows.length > 0) this.ui.leaderboard = rows;
  }

  private async fetchServerLeaderboard(): Promise<void> {
    const ui = this.ui;
    const url = `${this.platform.resolveHttpBase()}/api/leaderboard`;
    const res = await this.platform.httpJson("GET", url);
    if (!ui.showLeaderboard) return;
    if (!res.ok) {
      ui.leaderboard = [];
      ui.leaderboardHint = "榜单暂未挂出";
      return;
    }
    ui.leaderboard = normalizeLeader(res.body);
    ui.leaderboardHint = ui.leaderboard.length === 0 ? "尚无茶客上榜" : "浏览器预览为全服榜，微信内为好友榜";
  }

  private onAuthOk(msg: ServerMessage): void {
    const p = asPayload(msg, "AUTH_OK");
    this.ui.userId = p.userId;
    this.ui.token = p.token;
    this.ui.authed = true;
    const serverWins = typeof p.wins === "number" && Number.isFinite(p.wins) ? p.wins : 0;
    this.persistWins(Math.max(serverWins, this.rememberedWins()));
    this.syncClock(p.serverTime);
    this.platform.setStorage("gaoya.nickname", this.ui.nickname);
    this.platform.setStorage("gaoya.avatar", this.ui.avatarUrl);
    this.platform.hideUserInfoButton();
    if (
      this.allowRoomRestore &&
      this.ui.roomId &&
      this.ui.token &&
      (this.ui.screen === "battle" || this.ui.screen === "result")
    ) {
      this.socket.send("RECONNECT", { token: this.ui.token, roomId: this.ui.roomId });
      return;
    }
    this.tryPendingJoin();
    if (this.ui.screen === "boot" || this.ui.screen === "login") {
      this.ui.connHint = this.hasResume() ? "正在恢复对局…" : this.ui.connHint;
      this.scheduleResumeFallback();
    }
  }

  private onRoomCreated(msg: ServerMessage): void {
    const p = asPayload(msg, "ROOM_CREATED");
    if (!this.shouldEnterRoom()) {
      this.declineRestoredRoom(p.roomId);
      return;
    }
    const sameCode = this.ui.roomCode === p.roomCode;
    this.ui.roomCode = p.roomCode;
    this.ui.shareTicket = p.shareTicket;
    if (p.roomId) this.ui.roomId = p.roomId;
    this.ui.privateMode = "wait";
    this.ui.screen = "private";
    this.clearResumeFallback();
    this.markRoomStay();
    this.rememberBattle();
    this.refreshShareMenu();
    if (!this.ui.publicMatch && !sameCode) {
      toast(this.ui, this.ui.mode === "RUSH" ? "茶桌已开，把房号递给朋友" : "雅间已备好，把房号递给朋友");
    }
  }

  private onRoomLobby(msg: ServerMessage): void {
    const p = asPayload(msg, "ROOM_LOBBY");
    if (!this.shouldEnterRoom()) {
      this.declineRestoredRoom(p.roomId);
      return;
    }
    this.ui.mode = p.mode;
    this.ui.roomId = p.roomId;
    this.ui.roomCode = p.roomCode;
    this.ui.lobbyHostId = p.hostId;
    this.ui.lobbySeats = p.seats;
    this.ui.lobbyMaxSeats = p.maxSeats || 4;
    this.ui.publicMatch = !!p.publicMatch;
    const wasReadyWait = this.ui.lobbyReadyDeadline > 0;
    this.ui.lobbyCountdownDeadline = p.countdownDeadline ?? 0;
    this.ui.lobbyReadyDeadline = p.readyDeadline ?? 0;
    this.syncClock(p.serverTime);
    this.ui.privateMode = "wait";
    this.ui.screen = "private";
    this.clearResumeFallback();
    this.markRoomStay();
    this.rememberBattle();
    this.refreshShareMenu();
    if (this.ui.lobbyReadyDeadline > 0 && !wasReadyWait) {
      toast(this.ui, "人满，请准备");
    }
    for (const seat of p.seats) {
      if (seat.avatar) this.loadPeerAvatar(seat.id, seat.avatar);
    }
  }

  private onMatchSuccess(msg: ServerMessage): void {
    const p = asPayload(msg, "MATCH_SUCCESS");
    if (!this.shouldEnterRoom()) {
      this.declineRestoredRoom(p.roomId);
      return;
    }
    this.ui.roomId = p.roomId;
    this.ui.mode = p.mode;
    if (p.roomCode) this.ui.roomCode = p.roomCode;
    else if (!this.ui.roomCode && /^\d{6}$/.test(this.ui.joinInput)) this.ui.roomCode = this.ui.joinInput;
    this.ui.opponent = p.opponent ?? null;
    this.ui.peers = (p.players ?? []).filter((pl) => pl.id !== this.ui.userId);
    this.ui.scores = { ...(p.scores ?? {}) };
    if (!(this.ui.userId in this.ui.scores)) this.ui.scores[this.ui.userId] = 0;
    for (const peer of this.ui.peers) {
      if (!(peer.id in this.ui.scores)) this.ui.scores[peer.id] = 0;
    }
    this.ui.matchDeadline = p.matchDeadline ?? 0;
    if (p.mode === "RUSH" && p.players && p.players.length >= 3) {
      this.ui.lobbyMaxSeats = p.players.length === 3 ? 3 : 4;
    }
    this.ui.firstPlayerId = p.firstPlayerId;
    this.ui.selfHp = p.initialHp;
    this.ui.oppHp = p.initialHp;
    this.ui.selfPunish = 0;
    this.ui.oppPunish = 0;
    this.ui.round = 0;
    this.ui.currentTurnId = "";
    this.ui.lastWord = "";
    this.ui.lastPinyins = [];
    this.ui.wordChain = [];
    this.ui.scrollX = 0;
    this.ui.scrollFollow = true;
    this.ui.usedWords = [];
    this.ui.deadline = 0;
    this.ui.turnMs = 0;
    this.ui.opponentOnline = true;
    this.ui.gameOver = null;
    this.ui.hintsLeft = p.mode === "RUSH" ? 0 : (p.hintsLeft ?? HINTS_PER_MATCH);
    this.ui.hintWord = null;
    this.ui.hintPending = false;
    this.ui.hintPendingUntil = 0;
    this.ui.scrambleItemsLeft = p.mode === "RUSH" ? 0 : (p.scrambleItemsLeft ?? SCRAMBLE_ITEMS_PER_MATCH);
    this.ui.selfScramble = null;
    this.ui.oppScramble = null;
    this.ui.scramblePending = false;
    this.ui.scramblePendingUntil = 0;
    this.ui.scorePop = null;
    this.ui.selfRacing = true;
    this.ui.screen = "battle";
    resetBattleInput(this.ui);
    this.markRoomStay();
    this.rememberBattle();
    if (p.opponent) this.loadOppAvatar(p.opponent.avatar);
    for (const peer of this.ui.peers) {
      this.ui.peerOnline[peer.id] = true;
      if (peer.avatar) this.loadPeerAvatar(peer.id, peer.avatar);
    }
    banner(this.ui, p.mode === "RUSH" ? "开抢" : "茶战开席");
    this.platform.playSfx(SFX.start);
    this.refreshShareMenu();
  }

  private onRoundStart(msg: ServerMessage): void {
    this.markSocketAuthed();
    const p = asPayload(msg, "ROUND_START") as RoundStartPayload;
    this.applyRound(p);
    if (p.reason === "DEAD_END_RESET") banner(this.ui, this.ui.mode === "RUSH" ? "此路无人接，换一巡茶" : "此路不通，换一巡茶");
    else if (p.reason === "BOTH_STUCK_RESET") {
      banner(this.ui, "此词双方都接不上，过掉");
      toast(this.ui, "双方都接不上，已换词", 2800);
    }
    else if (p.reason === "GAME_START") banner(this.ui, "请接龙");
    if (this.ui.mode !== "RUSH" && isMyTurn(this.ui) && p.reason !== "GAME_START") {
      this.platform.playSfx(SFX.turn);
    }
  }

  private applyRound(p: RoundStartPayload): void {
    this.syncClock(p.serverTime);
    this.ui.round = p.round;
    this.ui.currentTurnId = p.currentTurnId;
    this.ui.lastWord = p.lastWord;
    this.ui.lastPinyins = p.lastPinyins;
    this.ui.deadline = p.deadline;
    this.ui.turnMs = p.turnMs;
    if (p.lastWord && !this.ui.wordChain.includes(p.lastWord)) {
      this.ui.wordChain = this.ui.wordChain.length === 0 ? [p.lastWord] : [...this.ui.wordChain, p.lastWord];
      this.ui.scrollFollow = true;
    }
    const hadSelf = (this.ui.selfScramble?.turnsLeft ?? 0) > 0;
    this.applyScrambles(p.scrambles ?? []);
    if (hadSelf && !this.ui.selfScramble) toast(this.ui, "活字盘已归位");
    resetBattleInput(this.ui);
  }

  private applyScrambles(list: readonly ScrambleEffect[]): void {
    this.ui.selfScramble = null;
    this.ui.oppScramble = null;
    for (const s of list) {
      const pack = { seed: s.seed, turnsLeft: s.turnsLeft };
      if (s.targetId === this.ui.userId) this.ui.selfScramble = pack;
      else this.ui.oppScramble = pack;
    }
  }

  private onCandidates(msg: ServerMessage): void {
    const p = asPayload(msg, "CANDIDATES");
    if (p.round !== this.ui.round) return;
    if (p.abbr !== this.ui.abbr && p.abbr !== this.ui.lastQueriedAbbr) return;
    const hinted = this.ui.hintWord;
    const words = p.words.slice(0, 5);
    this.ui.candidates = hinted && !words.includes(hinted) ? [hinted, ...words].slice(0, 5) : words;
    if (this.ui.candidates.length === 0 && countAbbrSyllables(this.ui.abbr) >= 4) {
      toast(this.ui, "没有匹配的成语");
    }
  }

  private onHintResult(msg: ServerMessage): void {
    const p = asPayload(msg, "HINT_RESULT") as HintResultPayload;
    this.ui.hintPending = false;
    this.ui.hintPendingUntil = 0;
    if (p.round !== this.ui.round) return;
    this.ui.hintsLeft = p.hintsLeft;
    this.ui.hintWord = p.word;
    this.ui.candidates = [p.word];
    banner(this.ui, `锦囊荐：${p.word}`);
    toast(this.ui, `锦囊荐：${p.word}`);
    this.platform.playSfx(SFX.hint);
    this.submitWord(p.word);
  }

  private onItemApplied(msg: ServerMessage): void {
    const p = asPayload(msg, "ITEM_APPLIED") as ItemAppliedPayload;
    if (p.itemId !== ITEM_SCRAMBLE) return;
    const pack = { seed: p.seed, turnsLeft: p.turnsLeft };
    if (p.fromId === this.ui.userId) {
      this.ui.scrambleItemsLeft = p.itemsLeft;
      this.ui.scramblePending = false;
      this.ui.scramblePendingUntil = 0;
      this.ui.oppScramble = pack;
      banner(this.ui, "已打乱对方活字盘");
      toast(this.ui, "对方两手内 26 键全乱", 2800);
      this.platform.playSfx(SFX.hint);
    }
    if (p.targetId === this.ui.userId) {
      this.ui.selfScramble = pack;
      resetBattleInput(this.ui);
      banner(this.ui, "你的活字盘被打乱");
      toast(this.ui, "两手内字母全乱，看键输入", 2800);
      this.platform.playSfx(SFX.miss);
    }
  }

  private onActionResult(msg: ServerMessage): void {
    this.markSocketAuthed();
    const p = asPayload(msg, "ACTION_RESULT");
    this.ui.lastActionFlash = {
      word: p.word,
      ok: p.success,
      until: this.platform.now() + 900,
      playerId: p.playerId,
    };
    if (p.success) {
      this.platform.playSfx(SFX.success);
      if (p.scores) {
        const prev = this.ui.scores[p.playerId] ?? 0;
        const next = p.scores[p.playerId] ?? prev;
        this.ui.scores = p.scores;
        if (this.ui.mode === "RUSH" && next > prev) {
          this.ui.scorePop = { playerId: p.playerId, until: this.platform.now() + 1200 };
        }
      }
      if (p.word && !this.ui.wordChain.includes(p.word)) {
        this.ui.wordChain.push(p.word);
        this.ui.scrollFollow = true;
      }
      if (p.word && !this.ui.usedWords.includes(p.word)) this.ui.usedWords.push(p.word);
      this.ui.lastWord = p.word;
      resetBattleInput(this.ui);
    } else if (p.reason) {
      if (p.playerId === this.ui.userId) {
        this.platform.playSfx(SFX.miss);
        toast(this.ui, REJECT_TEXT[p.reason]);
      }
    }
  }

  private onPunishment(msg: ServerMessage): void {
    const p = asPayload(msg, "PUNISHMENT") as PunishmentPayload;
    if (p.targetId === this.ui.userId) {
      this.ui.selfHp = p.hpLeft;
      this.ui.selfPunish = p.punishLevel;
    } else {
      this.ui.oppHp = p.hpLeft;
      this.ui.oppPunish = p.punishLevel;
    }
    this.ui.shakeUntil = this.platform.now() + 100;
    if (p.reason === "TIMEOUT") this.platform.playSfx(SFX.timeout);
    const who = p.targetId === this.ui.userId ? "你" : "对方";
    const why = p.reason === "TIMEOUT" ? "香燃尽" : "答错";
    toast(this.ui, `${who}倾覆一杯（${why}）`);
  }

  private onGameOver(msg: ServerMessage): void {
    this.markSocketAuthed();
    const p = asPayload(msg, "GAME_OVER") as GameOverPayload;
    if (!this.shouldEnterRoom()) {
      this.declineRestoredRoom(this.ui.roomId);
      return;
    }
    this.ui.gameOver = p;
    if (p.wordChain.length > 0) this.ui.wordChain = p.wordChain;
    this.ui.screen = "result";
    this.ui.deadline = 0;
    this.ui.scrollY = 0;
    this.clearResumeFallback();
    this.markRoomStay();
    this.forgetBattle();
    this.platform.playSfx(
      p.winnerId === this.ui.userId || (p.ranking ?? []).find((r) => r.userId === this.ui.userId)?.rank === 1
        ? SFX.win
        : SFX.lose,
    );
    this.refreshShareMenu();
    const roomId = this.ui.roomId;
    const won =
      p.winnerId === this.ui.userId ||
      (p.ranking ?? []).find((r) => r.userId === this.ui.userId)?.rank === 1;
    const awarded =
      typeof p.winDelta?.[this.ui.userId] === "number"
        ? p.winDelta[this.ui.userId]!
        : won
          ? 1
          : 0;
    if (awarded > 0 && roomId && this.countedWinRoom !== roomId) {
      this.countedWinRoom = roomId;
      if (awarded >= 2) toast(this.ui, "今日首次与好友完局 · 胜场翻倍", 2400);
      this.persistWins(this.ui.totalWins + awarded, true);
    }
  }

  private rememberedWins(): number {
    const raw = this.platform.getStorage("gaoya.wins");
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  private persistWins(wins: number, announceHonor = false): void {
    const prev = this.ui.totalWins;
    const n = Math.max(this.rememberedWins(), Math.max(0, Math.floor(wins)));
    this.ui.totalWins = n;
    this.platform.setStorage("gaoya.wins", String(n));
    this.platform.publishWins(n);
    if (!announceHonor) return;
    const promoted = honorPromoted(prev, n);
    if (promoted) toast(this.ui, `荣升「${promoted.name}」`, 2800);
  }

  private onSnapshot(msg: ServerMessage): void {
    this.markSocketAuthed();
    const p = asPayload(msg, "ROOM_SNAPSHOT") as RoomSnapshotPayload;
    if (!this.shouldEnterRoom()) {
      this.declineRestoredRoom(p.roomId);
      return;
    }
    this.syncClock(p.serverTime);
    this.ui.roomId = p.roomId;
    this.ui.mode = p.mode;
    this.ui.round = p.round;
    this.ui.selfHp = p.you.hp;
    this.ui.selfPunish = INITIAL_HP - p.you.hp;
    if (p.scores) this.ui.scores = p.scores;
    else if (typeof p.you.score === "number") this.ui.scores = { ...this.ui.scores, [p.you.userId]: p.you.score };
    this.ui.matchDeadline = p.matchDeadline ?? 0;
    if (p.players) {
      this.ui.peers = p.players
        .filter((pl) => pl.userId !== this.ui.userId)
        .map((pl) => ({ id: pl.userId, nickname: pl.nickname, avatar: pl.avatar }));
      for (const pl of p.players) {
        this.ui.peerOnline[pl.userId] = pl.online;
        this.ui.scores[pl.userId] = pl.score;
        if (pl.userId === this.ui.userId) this.ui.selfRacing = pl.racing;
        if (pl.avatar) this.loadPeerAvatar(pl.userId, pl.avatar);
      }
    }
    if (p.opponent) {
      this.ui.oppHp = p.opponent.hp;
      this.ui.oppPunish = INITIAL_HP - p.opponent.hp;
      this.ui.opponent = {
        id: p.opponent.userId,
        nickname: p.opponent.nickname,
        avatar: p.opponent.avatar || "",
      };
      this.ui.opponentOnline = p.opponent.online;
      this.loadOppAvatar(p.opponent.avatar || "");
    }
    this.ui.currentTurnId = p.currentTurnId;
    this.ui.lastWord = p.lastWord;
    this.ui.lastPinyins = p.lastPinyins;
    this.ui.deadline = p.deadline;
    this.ui.turnMs = p.turnMs;
    this.ui.usedWords = p.usedWords;
    this.ui.wordChain = p.wordChain;
    this.ui.scrollFollow = true;
    this.ui.hintsLeft = p.you.hintsLeft ?? HINTS_PER_MATCH;
    this.ui.scrambleItemsLeft = p.mode === "RUSH" ? 0 : (p.you.scrambleItemsLeft ?? SCRAMBLE_ITEMS_PER_MATCH);
    this.ui.selfScramble = p.you.scramble ? { seed: p.you.scramble.seed, turnsLeft: p.you.scramble.turnsLeft } : null;
    this.ui.oppScramble = p.opponent?.scramble
      ? { seed: p.opponent.scramble.seed, turnsLeft: p.opponent.scramble.turnsLeft }
      : null;
    this.ui.scramblePending = false;
    this.ui.scramblePendingUntil = 0;
    resetBattleInput(this.ui);
    const fromCold = this.ui.screen === "login" || this.ui.screen === "boot" || this.ui.screen === "hall";
    if (p.state === "ENDED") {
      this.ui.screen = "result";
      this.ui.scrollY = 0;
    } else if (p.mode === "RUSH" && p.state === "CREATED") {
      this.ui.screen = "private";
      this.ui.privateMode = "wait";
    } else {
      this.ui.screen = "battle";
    }
    this.clearResumeFallback();
    this.markRoomStay();
    this.rememberBattle();
    if (fromCold && p.state !== "ENDED") toast(this.ui, "对局已恢复");
    if (p.state === "PLAYING" && this.ui.mode === "RUSH" && !this.ui.selfRacing) {
      toast(this.ui, "你已离席，本局观战", 2800);
    }
  }

  private onOppOffline(msg: ServerMessage): void {
    const p = asPayload(msg, "OPPONENT_OFFLINE");
    this.ui.opponentOnline = false;
    this.ui.opponentGraceDeadline = p.graceDeadline;
    this.ui.peerOnline[p.opponentId] = false;
    toast(this.ui, this.ui.mode === "RUSH" ? "有人暂时离席" : "对方暂时离席", 3000);
  }

  private onOppOnline(msg: ServerMessage): void {
    const p = asPayload(msg, "OPPONENT_ONLINE");
    this.ui.opponentOnline = true;
    this.ui.opponentGraceDeadline = 0;
    this.ui.peerOnline[p.opponentId] = true;
    toast(this.ui, this.ui.mode === "RUSH" ? "有人归座" : "对方归座");
  }

  private onError(p: ErrorPayload): void {
    this.ui.hintPending = false;
    this.ui.hintPendingUntil = 0;
    this.ui.scramblePending = false;
    this.ui.scramblePendingUntil = 0;
    const { code, message } = p;
    if (code === ERROR_CODES.PROTOCOL_MISMATCH) {
      toast(this.ui, "请更新小游戏");
      return;
    }
    if (code === ERROR_CODES.UNAUTHENTICATED) {
      this.ui.authDevFallback = true;
      void this.sendAuth();
      return;
    }
    if (code === ERROR_CODES.AUTH_FAILED) {
      if (this.platform.kind === "wechat" && !this.ui.authDevFallback) {
        if (!this.authLoginRetry) {
          this.authLoginRetry = true;
          void this.sendAuth();
          return;
        }
        this.ui.authDevFallback = true;
        void this.sendAuth();
        return;
      }
      toast(this.ui, this.ui.useWxProfile ? "微信登录失败，请再试一次" : "雅号未通过，请换一个");
      this.ui.connecting = false;
      this.ui.screen = "login";
      return;
    }
    if (code === ERROR_CODES.NOT_YOUR_TURN || code === ERROR_CODES.DUPLICATE) return;
    if (code === ERROR_CODES.HINTS_EXHAUSTED) {
      toast(this.ui, "本局锦囊已经用完");
      return;
    }
    if (code === ERROR_CODES.HINT_UNAVAILABLE) {
      toast(this.ui, "此路暂无可荐之词");
      return;
    }
    if (code === ERROR_CODES.ITEM_EXHAUSTED) {
      toast(this.ui, "本局乱序已经用完");
      return;
    }
    if (code === ERROR_CODES.ITEM_UNAVAILABLE) {
      toast(this.ui, message || "现在不能打乱对方键盘");
      return;
    }
    if (code === ERROR_CODES.ROOM_GONE) {
      if (this.ui.screen === "private" && !this.ui.publicMatch) {
        const rush = this.ui.mode === "RUSH";
        const seats = this.ui.lobbyMaxSeats === 3 ? 3 : 4;
        toast(this.ui, "原桌已散，正在新开一桌");
        if (rush) this.createRushRoom(seats);
        else this.createRoom();
        return;
      }
      toast(this.ui, "雅间已散");
      this.backHall();
      return;
    }
    if (code === ERROR_CODES.RECONNECT_FAILED) {
      this.clearResumeFallback();
      this.forgetBattle();
      this.ui.roomId = "";
      this.ui.token = this.ui.authed ? this.ui.token : "";
      if (this.ui.gameOver) {
        this.ui.screen = "result";
        if (!this.ui.authed) void this.sendAuth();
        toast(this.ui, "对局已结束");
        return;
      }
      toast(this.ui, this.ui.authed ? "对局已散，回到茶馆" : "对局已散，正在入座…");
      if (!this.ui.authed) {
        this.ui.connHint = this.ui.useWxProfile ? "正在用微信身份入座…" : "正在推门…";
        this.leaveToHall();
        void this.sendAuth();
        return;
      }
      this.leaveToHall();
      return;
    }
    if (code === ERROR_CODES.ROOM_CODE_INVALID) {
      toast(this.ui, "房号不对或已满座");
      return;
    }
    if (code === ERROR_CODES.ROOM_STARTED) {
      toast(this.ui, "对局已经开始");
      return;
    }
    if (code === ERROR_CODES.RUSH_AFK_KICK) {
      toast(this.ui, message || "超时未准备，已被请出");
      this.leaveToHall();
      return;
    }
    if (code === ERROR_CODES.RUSH_BANNED) {
      toast(this.ui, message || "连续两次未准备，暂不得进入抢答", 4000);
      this.leaveToHall();
      return;
    }
    if (code === ERROR_CODES.NICKNAME_BLOCKED) {
      toast(this.ui, "这个雅号不宜使用");
      this.ui.connecting = false;
      this.ui.screen = "login";
      return;
    }
    toast(this.ui, message || "茶馆出了点岔子");
  }

  private syncClock(serverTime: number): void {
    this.ui.clockOffset = serverTime - this.platform.now();
  }

  private shouldEnterRoom(): boolean {
    return this.liveIntent || this.allowRoomRestore;
  }

  private noteLiveIntent(): void {
    this.liveIntent = true;
  }

  private markRoomStay(): void {
    this.allowRoomRestore = true;
    saveStay((key, value) => this.platform.setStorage(key, value), "room");
  }

  private markAwayStay(): void {
    this.liveIntent = false;
    this.allowRoomRestore = false;
    saveStay((key, value) => this.platform.setStorage(key, value), "away");
  }

  private declineRestoredRoom(roomId?: string): void {
    this.socket.send("LEAVE_ROOM", roomId ? { roomId } : {});
    this.leaveToHall();
  }

  private hasResume(): boolean {
    return Boolean(this.ui.token && this.ui.roomId);
  }

  private rememberBattle(): void {
    if (!this.ui.token || !this.ui.roomId) return;
    saveResume((key, value) => this.platform.setStorage(key, value), {
      token: this.ui.token,
      roomId: this.ui.roomId,
      userId: this.ui.userId,
    });
  }

  private forgetBattle(): void {
    clearResume((key, value) => this.platform.setStorage(key, value));
  }

  private scheduleResumeFallback(): void {
    this.clearResumeFallback();
    this.resumeFallbackId = setTimeout(() => {
      this.resumeFallbackId = null;
      if (this.ui.screen === "boot" || this.ui.screen === "login") {
        this.ui.screen = "hall";
        this.ui.connHint = "";
      }
    }, 800);
  }

  private clearResumeFallback(): void {
    if (this.resumeFallbackId === null) return;
    clearTimeout(this.resumeFallbackId);
    this.resumeFallbackId = null;
  }

  applyWxProfile(nickname: string, avatar: string): void {
    const nick = clipNickname(nickname);
    if (!nick) return;
    this.ui.nickname = nick;
    this.ui.avatarUrl = avatar;
    this.ui.wxProfileReady = true;
    this.platform.setStorage("gaoya.nickname", nick);
    this.platform.setStorage("gaoya.avatar", avatar);
    this.loadSelfAvatar(avatar);
    this.tryResumeAfterProfile();
  }

  tryResumeAfterProfile(): void {
    if (this.ui.authed || this.ui.connecting) return;
    if (this.ui.screen !== "login" && this.ui.screen !== "boot") return;
    if (!this.hasResume() && !this.allowRoomRestore) return;
    if (this.ui.useWxProfile && (!this.ui.privacyAgreed || !this.ui.wxProfileReady)) return;
    this.enterTeahouse();
  }

  loadSelfAvatar(url: string): void {
    const src = this.mediaUrl(url);
    if (!src) {
      this.ui.avatarImg = null;
      return;
    }
    void this.platform.loadImage(src).then((img) => {
      if (this.ui.avatarUrl === url) this.ui.avatarImg = img;
    });
  }

  private loadOppAvatar(url: string): void {
    const src = this.mediaUrl(url);
    if (!src) {
      this.ui.oppAvatarImg = null;
      return;
    }
    void this.platform.loadImage(src).then((img) => {
      if (this.ui.opponent && this.ui.opponent.avatar === url) this.ui.oppAvatarImg = img;
    });
  }

  private loadPeerAvatar(userId: string, url: string): void {
    const src = this.mediaUrl(url);
    if (!src) return;
    void this.platform.loadImage(src).then((img) => {
      this.ui.peerImgs = { ...this.ui.peerImgs, [userId]: img };
    });
  }

  /** 机器人头像在服务端 `/avatars/`，相对路径要拼 HTTP_BASE。 */
  private mediaUrl(url: string): string {
    if (!url) return url;
    if (/^https?:\/\//i.test(url) || url.startsWith("wxfile:") || url.startsWith("assets/")) return url;
    if (url.startsWith("/")) return `${this.platform.resolveHttpBase()}${url}`;
    return url;
  }
}

function normalizeLeader(body: unknown): LeaderRow[] {
  const arr = Array.isArray(body)
    ? body
    : body && typeof body === "object" && Array.isArray((body as { users?: unknown }).users)
      ? (body as { users: unknown[] }).users
      : body && typeof body === "object" && Array.isArray((body as { list?: unknown }).list)
        ? (body as { list: unknown[] }).list
        : [];
  const out: LeaderRow[] = [];
  for (const row of arr) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const nickname = String(r["nickname"] ?? r["name"] ?? "茶客");
    const wins = Number(r["wins"] ?? r["total_wins"] ?? r["totalWins"] ?? 0);
    const safeWins = Number.isFinite(wins) ? wins : 0;
    const titleRaw = r["title"];
    const entry: LeaderRow = { nickname, wins: safeWins };
    if (typeof titleRaw === "string" && titleRaw) entry.title = titleRaw;
    out.push(entry);
  }
  return out.slice(0, 20);
}
